import * as React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { atom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';

import { FormLabelWrapper, HelperTextRElement, Text, TextArea } from '@restworld/ui-ds-base';

import { EditModalFooter, EditModalTitle, IEditModalProps } from '../profile-edit-modal';
import { getWorkerAtom } from '../../../global-state.worker-data';
import { useApiSubmitHandler } from '../../hooks';

const defaultValuesAtom = atom((get) => {
  const { data: workerData } = get(getWorkerAtom);
  return {
    motivation_text: workerData?.motivation_text,
    presentation: workerData?.presentation,
  };
});
type InputProps = {
  motivation_text: string;
  presentation: string;
};
type IPresentationFormContentProps = IEditModalProps;
export const PresentationFormContent: React.FunctionComponent<IPresentationFormContentProps> = ({
  onClose,
  editPageTitle,
}) => {
  const { t } = useTranslation();
  const defaultValues = useAtomValue(defaultValuesAtom);
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<InputProps>({
    reValidateMode: 'onSubmit',
    defaultValues,
  });
  const { apiSubmitHandler, loading } = useApiSubmitHandler({ onClose, editPageType: 'presentation' });

  const onSubmit: SubmitHandler<InputProps> = React.useCallback(
    (params) => apiSubmitHandler(params),
    [apiSubmitHandler]
  );

  return (
    <div className="w-full /* p-6 bg-primary-50 */">
      <EditModalTitle onClose={onClose} editPageTitle={editPageTitle} />
      <Text>{t('onboarding-page:introduce_yourself_text')}</Text>
      <form autoComplete="off" method="post" onSubmit={handleSubmit(onSubmit)} className="flex gap-4 flex-col">
        <FormLabelWrapper id="presentation" label={t('common:cover_letter')}>
          <Controller
            name="presentation"
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <TextArea
                id="presentation"
                placeholder={t('onboarding-page:describe_in_1600_characters')}
                supportText={
                  Object.keys(errors).includes('presentation') ? t('onboarding-page:this_field_is_required') : undefined
                }
                isError={Object.keys(errors).includes('presentation')}
                {...field}
              />
            )}
          />
        </FormLabelWrapper>
        <FormLabelWrapper id="motivation_text" label={`${t('onboarding-page:why_are_you_looking_for_work')}?`}>
          <Controller
            name="motivation_text"
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <TextArea
                id="motivation_text"
                placeholder={t('onboarding-page:describe_your_reasons_for_looking_for_work')}
                supportText={
                  Object.keys(errors).includes('motivation_text')
                    ? t('onboarding-page:this_field_is_required')
                    : undefined
                }
                isError={Object.keys(errors).includes('motivation_text')}
                {...field}
              />
            )}
          />
        </FormLabelWrapper>
        <EditModalFooter
          onClose={() => {
            reset(defaultValues);
            onClose();
          }}
          loading={loading}
          editPageType="presentation"
        />
      </form>
    </div>
  );
};

export default PresentationFormContent;
